import { MouseEvent, memo, useRef } from 'react';
import cn from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { Text, Icon, Button } from '@pairy/pds-react';
import { useIsomorphicLayoutEffect } from '@pairy/react-hooks';

import useUpdateViewerPageState from '../../../../hooks/useUpdateViewerPageState';
import { CommonPageNavigationProps } from '../index.types';
import {
  isDisableHelpersAtom,
  isFirstPageImageLoadedAtom,
  isOpenDrawerAtom,
} from '../../../../store/index.atom';

import * as Styled from './index.styles';

export interface DesktopPageNavigationProps extends CommonPageNavigationProps {
  translateKoToEn: (item: string) => string;
  blindPages?: number[] | null;
}

const DesktopPageNavigation = ({
  fileName,
  pages,
  blindPages,
  translateKoToEn,
}: DesktopPageNavigationProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isDisableHelpers, setIsDisableHelpers] = useAtom(isDisableHelpersAtom);
  const [isOpenDrawer, setIsOpenDrawer] = useAtom(isOpenDrawerAtom);

  const { updateCurrentPage, currentPage } = useUpdateViewerPageState();

  useIsomorphicLayoutEffect(() => {
    if (!wrapperRef.current) {
      return;
    }

    const currentPageElement = document.querySelector(
      `[data-current-page="true"]`,
    ) as HTMLDivElement;

    if (!(currentPageElement instanceof HTMLDivElement)) {
      return;
    }

    wrapperRef.current.scrollTo({
      top: currentPageElement.offsetTop - 80,
    });
  }, [wrapperRef.current, currentPage]);

  const handleClick = (page: number) => {
    updateCurrentPage(page);
  };

  const handleClickOpenDrawerButton = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    if (isDisableHelpers) {
      setIsDisableHelpers(false);
      return;
    }

    setIsOpenDrawer(true);
  };

  const handleClickWrapper = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const isFirstPageImageLoaded = useAtomValue(isFirstPageImageLoadedAtom);

  if (!isFirstPageImageLoaded) {
    return null;
  }

  return (
    <>
      <Styled.ButtonWrapper
        className={isDisableHelpers ? 'disable' : ''}
        onClick={handleClickOpenDrawerButton}
      >
        <Styled.ButtonOpenDrawer
          className={isOpenDrawer ? 'hidden' : 'visible'}
          variant="zinc_500"
          textVariant="lg-medium"
          paddingY={16}
          rightIcon={<Icon icon="ph:caret-double-right" size={19} />}
        >
          {translateKoToEn('pageList')}
        </Styled.ButtonOpenDrawer>
      </Styled.ButtonWrapper>
      <Styled.Wrapper
        ref={wrapperRef}
        className={isOpenDrawer ? 'visible' : 'hidden'}
        onClick={handleClickWrapper}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      >
        <Styled.FlexCloseButton
          className={isOpenDrawer ? 'visible' : 'hidden'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Text variant="lg-medium" marginRight={8}>
            {fileName}
          </Text>
          <Button
            width="auto"
            onClick={() => setIsOpenDrawer(false)}
            paddingX={0}
            maxContentWidth
          >
            <Icon icon="ph:caret-double-left" size={19} color="zinc_400" />
            <Text variant="lg-medium" color="zinc_400">
              {translateKoToEn('desktop-pageList-close')}
            </Text>
          </Button>
        </Styled.FlexCloseButton>
        <Styled.StackPageWrapper direction="column">
          {pages?.map(({ page, thumnail, imageSources }) => {
            return (
              <Styled.Page
                key={page}
                className={currentPage === page ? 'current-page' : ''}
                data-current-page={currentPage === page}
                onClick={() => {
                  /* NOTE: 첫 Blind Page는 Drawer로 이동이 가능하도록 */
                  if (blindPages?.[0] !== page && blindPages?.includes(page)) {
                    return;
                  }

                  handleClick(page);
                }}
              >
                <Styled.PageInnerWrapper
                  className={currentPage === page ? 'current-page' : ''}
                >
                  <Text
                    as="p"
                    variant="md-medium"
                    textAlign="start"
                    marginBottom={8}
                    color={currentPage === page ? 'skyblue_500' : 'zinc_400'}
                  >
                    {translateKoToEn('page')} {page}
                  </Text>
                  {imageSources ? (
                    <Styled.BlindPictureImage
                      className={cn({
                        'is-blind': blindPages?.includes(page),
                      })}
                      alt={`${page}페이지 이미지`}
                      thumb={imageSources.thumb}
                    />
                  ) : (
                    <Styled.BlindImage
                      className={cn({
                        'is-blind': blindPages?.includes(page),
                      })}
                      alt={`${page}페이지 이미지`}
                      src={thumnail}
                      layout="fill"
                      sizes="296px"
                      quality={50}
                      draggable={false}
                    />
                  )}
                </Styled.PageInnerWrapper>
              </Styled.Page>
            );
          })}
        </Styled.StackPageWrapper>
      </Styled.Wrapper>
    </>
  );
};

export default memo(DesktopPageNavigation);
